import React, {useEffect, useRef, useState} from "react";
import {Button, Col, DatePicker, Divider, Drawer, Form, Input, Row, Select, Typography} from "antd";
import PropTypes from "prop-types";
import dayjs from "dayjs";

import EngageCurrency from "./EngageCurrency";
import InputMoney from "./InputMoney";
import TaxCheckboxesInForm from "./TaxCheckboxesInForm";
import Comments from "../../containers/staff/Comments";
import {handleChargePostTaxCalc, handleChargePreTaxCalc, updateCharge} from "../../helpers/billing";
import {messageType, showMessages} from "../../helpers/message";
import {isTwoArrayIdentical} from "../../helpers/misc";
import {chargeCreatePostTaxDisclaimer, chargeUpdateSuccessMessage, greaterThanOrEqualZero} from "../../helpers/text";
import {dateFormat} from "../../lib/time";

import "./ChargeCreateEditDrawer.less";

const {Option} = Select;

const ChargeEditDrawer = ({charge, taxes, openDrawer, closeDrawer, refreshBalances, refreshCharges}) => {
    //region State/Variable setup
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [preTaxAmount, setPreTaxAmount] = useState(charge.amount);
    const [postTaxAmount, setPostTaxAmount] = useState(null);
    const [sumOfTaxes, setSumOfTaxes] = useState(null);
    const mobileMode = window.innerWidth <= 768;

    // Reconciled and invoiced charges cannot have most fields edited,
    // this includes 0 dollar charges which have no status.
    const disableField = charge.status === 'reconciled' || charge.status === 'invoiced';

    // We need to use ref instead of state to persist the values from the input
    // and not reset to initialState on any state update.
    const preTaxAmountRef = useRef(charge.amount);
    const postTaxAmountRef = useRef(null);

    // Deconstruct the charge prop's taxes to populate the initial value of taxesApplied ref.
    let taxAppliedObj = {};
    charge.taxes.forEach(tax => {taxAppliedObj[tax.name] = true})
    const taxesApplied = useRef(taxAppliedObj);

    // onMount run one calculation to fill the postTaxAmount field.
    useEffect(() => {
        handleChargePreTaxCalc(undefined, preTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
            taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused);
    }, [taxes])

    // We need to know what the last amount input field was changed
    // to determine which one to dynamically update on tax checkboxes ticks.
    const lastAmountInputFocused = useRef({});
    const [form] = Form.useForm();
    //endregion

    //region Event Handlers

    // Event handlers to hide the drawer component.
    const hideDrawer = () => {
        setPreTaxAmount(null);
        setPostTaxAmount(null);
        setSumOfTaxes(null);
        preTaxAmountRef.current = null;
        postTaxAmountRef.current = null;
        taxesApplied.current = {};
        form.resetFields();
        closeDrawer();
    };

    // Within each of the nested calc handlers below we need to update the inverse pre/post tax amount.
    // Then refer to the taxesApplied ref to see which taxes checkboxes have been checked.
    // Then the taxes prop to apply the tax rates or inverse them. Reminder we purposely use ref AND state for the amounts.

    // Event handler for the taxes checkboxes being checked or unchecked,
    // sets the taxesApplied ref. And fires the pre/post tax event handlers to calculate taxes.
    const handleTaxesApplied = (e) => {
        const {value, checked} = e.target;
        taxesApplied.current[value] = checked;

        // We only want to fire one event handler at a time, when the tax checkboxes are checked or unchecked.
        // Determine based off the last focused input box.
        if (lastAmountInputFocused.current === 'preTaxAmount'){
            handleChargePreTaxCalc(undefined, preTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
                taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused);
        } else if (lastAmountInputFocused.current === 'postTaxAmount') {
            handleChargePostTaxCalc(undefined, postTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
                taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused);
        }
    }

    // Sanity check to see if any data has been updated, if not throw 'Nothing to update warning'.
    const sanityCheck = (formData) => {
        // Charge is referring to the param passed into this component (ChargeEditDrawer),
        // formData is the data currently filling the edit form.
        if (charge) {
            const {amount, charged_at, type, name, taxes} = charge;
            if (
                amount === preTaxAmount &&
                dayjs(charged_at).tz().format('YYYY-MM-DD') === dayjs(formData['chargedAt']).tz().format('YYYY-MM-DD') &&
                type === formData['type'] &&
                name === formData['name'] &&
                isTwoArrayIdentical(taxes.map(tax => tax.name) , formData['taxes'])
            ) {
                return false;
            }
        }

        return true;
    }

    //endregion
    //region API functionality

    const handleFinish = async (values) => {
        const abortSignal = abortSignalRef.current;

        const chargedAt = values['Date'].format('YYYY-MM-DD HH:mm:ssZZ');
        const amount = preTaxAmount;
        const type = values['Category'];
        const name = values['Name'];
        const taxes = values['Taxes'];

        const data = {amount, chargedAt, type, name, taxes};

        const isValid = sanityCheck(data);

        if (!isValid) {
            showMessages('Nothing to update!', messageType.error);
            return;
        }

        try {
            setLoading(true)

            await updateCharge(charge.id, charge.status, data, abortSignal);
            showMessages(chargeUpdateSuccessMessage, messageType.success);

            setLoading(false);
            hideDrawer();
            refreshBalances(true);
            refreshCharges();
        } catch (error) {
            showMessages(error.message, messageType.error)
            setLoading(false);
        }
    }

    return (
        <>
        {
            charge &&
            <Drawer className="charge-create-edit-drawer"
                    width={mobileMode ? "100%" : "45%"}
                    title={`Edit Charge #${charge.id}`}
                    placement="right"
                    closable={true}
                    onClose={hideDrawer}
                    open={openDrawer}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="charge-edit"
                    onFinish={handleFinish}
                    initialValues={{
                        'Date': dayjs(charge.charged_at),
                        'Category': charge.type,
                        'Name': charge.name,
                        'Taxes': charge.taxes.map(tax => tax.name)
                    }}
                >
                    <Row className="full-width-row">
                        <Col>
                            <Form.Item
                              name="Date"
                              label="Date: "
                              rules={[{type: 'object', required: true}]}
                            >
                                <DatePicker
                                   disabled={disableField}
                                   className="full-width-date-picker"
                                   placeholder="DD MM YYYY"
                                   format={dateFormat}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row className="full-width-row">
                        <Col>
                            <Form.Item
                              name="Category"
                              label="Charge Category: "
                              rules={[{type: 'string', required: true}]}
                            >
                                <Select disabled={disableField} placeholder="Please select">
                                    <Option value="adjustment">Adjustment</Option>
                                    <Option value="fee">Fee</Option>
                                    <Option value="fine">Fine</Option>
                                    <Option value="membership">Membership</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row className="full-width-row">
                        <Col>
                            <Form.Item name="Name" label="Charge Name: ">
                                <Input disabled={disableField} placeholder="Type here"/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Form.Item
                              name="Amount"
                              label="Amount (before tax): "
                              rules={[{type: 'number', required: !preTaxAmount, min: 0}]}
                              validateStatus={preTaxAmount ? (preTaxAmount >= 0.00 ? '' : 'error') : ''}
                              help={greaterThanOrEqualZero}
                            >
                                <InputMoney amount={preTaxAmount}
                                            onChange={(e) => handleChargePreTaxCalc(e, preTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
                                              taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused)
                                            }
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <TaxCheckboxesInForm taxes={taxes} handleTaxesApplied={handleTaxesApplied} disabled={disableField}/>
                    <Row>
                        <Col span={24}>
                            <Form.Item
                              name="AmountAfterTax"
                              label="Total Amount (after tax): "
                              extra={chargeCreatePostTaxDisclaimer}
                              rules={[{type: 'number', min: 0}]}
                              validateStatus={postTaxAmount ? (postTaxAmount >= 0.00 ? '' : 'error') : ''}
                              help={greaterThanOrEqualZero}
                            >
                                <InputMoney amount={postTaxAmount}
                                            onChange={(e) => handleChargePostTaxCalc(e, postTaxAmountRef, setPreTaxAmount, setPostTaxAmount,
                                              taxes, taxesApplied, setSumOfTaxes, lastAmountInputFocused)
                                            }
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <div className="subtotal-container">
                        <Row justify="space-between">
                            <Col><Typography.Text strong>Subtotal</Typography.Text></Col>
                            <Col><EngageCurrency amount={preTaxAmount ? parseFloat(preTaxAmount) : 0}/></Col>
                        </Row>
                        <Row justify="space-between">
                            <Col><Typography.Text strong>Taxes</Typography.Text></Col>
                            <Col><EngageCurrency amount={sumOfTaxes ? sumOfTaxes : 0}/></Col>
                        </Row>
                        <Row justify="space-between">
                            <Col><Typography.Title level={5} className="subtotal-label">Total
                                Cost:</Typography.Title></Col>
                            <Col>
                                <Typography.Title level={5} className="subtotal-amount">
                                    <EngageCurrency
                                      amount={postTaxAmount ? parseFloat(postTaxAmount) : 0}
                                    />
                                </Typography.Title>
                            </Col>
                        </Row>
                    </div>
                    <Row>
                        <Button className="charge-create-edit-cancel-btn" onClick={hideDrawer}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                    </Row>
                </Form>
                <Divider />
                <Typography.Title level={4}>Comments</Typography.Title>
                <div className="charge-edit-comments-container">
                    <Comments
                      commentableType="App\Models\Billings\Charge"
                      commentableID={charge.id}
                    />
                </div>
            </Drawer>
        }
        </>
    )
}

export default ChargeEditDrawer;

ChargeEditDrawer.propTypes = {
    charge: PropTypes.object.isRequired,
    openDrawer: PropTypes.bool.isRequired,
    closeDrawer: PropTypes.func.isRequired,
    refreshBalances: PropTypes.func.isRequired,
    refreshCharges: PropTypes.func.isRequired,
    taxes: PropTypes.array.isRequired
};
